import axios from "axios";

export const IS_LOCAL_ENV =
  !process.env.NODE_ENV || process.env.NODE_ENV === 'development';

const API_BASE_URL = IS_LOCAL_ENV ? 
 "http://localhost:8000/api/v1" : process.env.REACT_APP_API_URL

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add token and disable caching
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Disable caching for GET requests
    if (config.method === "get") {
      config.headers["Cache-Control"] = "no-cache, no-store, must-revalidate";
      config.headers["Pragma"] = "no-cache";
      config.headers["Expires"] = "0";
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      // Check if this is an error from an external API (like Meta/Facebook)
      // External API errors have specific headers like 'debug-link' from Meta
      const isExternalApiError =
        error.response?.headers?.["debug-link"]?.includes("meta.com") ||
        error.response?.headers?.["x-fb-request-id"];

      // Only logout if it's a backend authentication error, not an external API error
      if (!isExternalApiError) {
        // Only redirect if not already on login page
        const currentPath = window.location.pathname;
        if (currentPath !== "/login") {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error.response?.data || error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post("/auth/login", credentials),
  register: (userData) => api.post("/auth/register", userData),
  createUser: (userData) => api.post("/auth/users", userData),
  getAllUsers: (params) => api.get("/auth/users", { params }),
  getUserById: (id) => api.get(`/auth/users/${id}`),
  updateUser: (id, data) => api.put(`/auth/users/${id}`, data),
  deleteUser: (id) => api.delete(`/auth/users/${id}`), // no use
  permanentDeleteUser: (id) => api.delete(`/auth/users/${id}/permanent`),
  resetUserPassword: (id) => api.post(`/auth/users/${id}/reset-password`), // no use
};

// Contacts API
export const contactsAPI = {
  getAll: (params) => api.get("/contacts", { params }),
  getOne: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post("/contacts", data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
  bulkDelete: (data) => api.post("/contacts/bulk-delete", data),
  bulkUpload: (contacts) => api.post("/contacts/bulk-upload", { contacts }),
  getHistory: (id) => api.get(`/contacts/${id}/history`), // no use
  assignTag: (id, tag_id) => api.post(`/contacts/${id}/tags`, { tag_id }),
  removeTag: (id, tagId) => api.delete(`/contacts/${id}/tags/${tagId}`),
  getContactEmailCampaigns: (id) => api.get(`/contacts/${id}/campaigns`, { params: { type: 'email' } }),
  getContactWhatsappCampaigns: (id) => api.get(`/contacts/${id}/campaigns`, { params: { type: 'whatsapp' } }),
  getContactDonations: (id) => api.get(`/contacts/${id}/donations`),
  getContactComments: (id) => api.get(`/contacts/${id}/comments`),
  createContactComment: (id, data) =>
    api.post(`/contacts/${id}/comments`, data),
  deleteContactComment: (id, commentId) =>
    api.delete(`/contacts/${id}/comments/${commentId}`),
  getContactReminders: (id) => api.get(`/contacts/${id}/reminders`),
};

// Campaigns API
export const campaignsAPI = {
  getAll: (params) => api.get("/campaigns", { params }),
  getOne: (id) => api.get(`/campaigns/${id}`),
  create: (data) => api.post("/campaigns", data),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  delete: (id) => api.delete(`/campaigns/${id}`),
  bulkDelete: (data) => api.post("/campaigns/bulk-delete", data),
  // getStats: (id) => api.get(`/campaigns/${id}/stats`), // DEPRECATED: Stats now included in getOne response
  getContacts: (id, params) => api.get(`/campaigns/${id}/contacts`, { params }), 
  // getEmailStatistics: (id) => api.get(`/campaigns/${id}/email-statistics`), // DEPRECATED: Email stats now included in getOne response
  getAllContacts: (params) =>
    api.get("/contacts", { params: { limit: 10000, ...params } }),
  enrollContacts: (id, criteria) =>
    api.post(`/campaigns/${id}/enroll`, criteria),
  unenrollContact: (id, contactId) =>
    api.delete(`/campaigns/${id}/contacts/${contactId}`),
  getEmailStatistics: () => api.get("/campaigns/statistics/email"),
  getWhatsAppStatistics: () => api.get("/campaigns/statistics/whatsapp"),
  getWhatsAppFailedStats: (id) => api.get(`/campaigns/${id}/whatsapp-failed-stats`),
  retryWhatsAppMessages: (id, data) => api.post(`/campaigns/${id}/retry-whatsapp`, data),
};

// Donations API
export const donationsAPI = {
  getAll: (params) => api.get("/donations", { params }),
  getById: (id) => api.get(`/donations/${id}`),
  getOne: (id) => api.get(`/donations/${id}`),
  create: (data) => api.post("/donations", data),
  update: (id, data) => api.put(`/donations/${id}`, data),
  delete: (id) => api.delete(`/donations/${id}`),
  bulkDelete: (data) => api.post("/donations/bulk-delete", data),
  getStatistics: () => api.get("/donations/statistics"),
  sendReceipt: (id) => api.post(`/donations/${id}/send-receipt`), // for now no use
};

// Transactions API
export const transactionsAPI = {
  getAll: (params) => api.get("/transactions", { params }),
  getOne: (id) => api.get(`/transactions/${id}`),
  create: (data) => api.post("/transactions", data),
  update: (id, data) => api.put(`/transactions/${id}`, data),
  updateStatus: (id, status) =>
    api.put(`/transactions/${id}/status`, { status }),
};

// Receipts API
export const receiptsAPI = {
  getAll: (params) => api.get("/receipts", { params }),
  getOne: (id) => api.get(`/receipts/${id}`),
  create: (data) => api.post("/receipts", data),
  downloadPDF: (id) => api.get(`/receipts/${id}/pdf`, { responseType: "blob" }),
  resend: (id) => api.post(`/receipts/${id}/resend`),
};

// Email API
export const emailAPI = {
  getAllTemplates: () => api.get("/emails/templates"),
  getTemplate: (id) => api.get(`/emails/templates/${id}`),
  createTemplate: (data) => api.post("/emails/templates", data),
  updateTemplate: (id, data) => api.put(`/emails/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/emails/templates/${id}`),
  sendBulk: (data) => api.post("/emails/send-bulk", data), // no use
  getLogs: (params) => api.get("/emails/logs", { params }), // no use
};

// Email Templates API (for template editor feature)
export const emailTemplatesAPI = {
  getAll: (params) => api.get("/email-templates", { params }),
  getById: (id) => api.get(`/email-templates/${id}`),
  create: (data) => api.post("/email-templates", data),
  update: (id, data) => api.put(`/email-templates/${id}`, data),
  delete: (id, params) => api.delete(`/email-templates/${id}`, { params }),
  bulkDelete: (data) => api.post("/email-templates/bulk-delete", data),
  preview: (id, sampleData) =>
    api.post(`/email-templates/${id}/preview`, { sampleData }),
  testSend: (id, to) => api.post(`/email-templates/${id}/test-send`, { to }),
};

// Upload API. // no use for now
export const uploadAPI = {
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append("image", file);
    return api.post("/uploads/images", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
};

// Custom Fields API (for email template placeholders) // doubt for now how it is use
export const customFieldsAPI = {
  getAll: () => api.get("/contacts/custom-fields"),
};

// Tags API
export const tagsAPI = {
  getAll: (params) => api.get("/tags", { params }),
  getOne: (id) => api.get(`/tags/${id}`),
  create: (data) => api.post("/tags", data),
  update: (id, data) => api.put(`/tags/${id}`, data),
  delete: (id) => api.delete(`/tags/${id}`),
  bulkDelete: (data) => api.post("/tags/bulk-delete", data),
  getContacts: (id) => api.get(`/tags/${id}/contacts`),
};

// Reminders API
export const remindersAPI = {
  getAll: (params) => api.get("/reminders", { params }),
  getStatistics: () => api.get("/reminders/statistics"),
  getOverdue: () => api.get("/reminders/overdue"),
  create: (data) => api.post("/reminders", data),
  update: (id, data) => api.put(`/reminders/${id}`, data),
  updateStatus: (id, status) => api.put(`/reminders/${id}/status`, { status }),
  delete: (id) => api.delete(`/reminders/${id}`),
  bulkDelete: (data) => api.delete("/reminders/bulk", { data }),
};

// Donation Schemas API
export const donationSchemasAPI = {
  getAll: (params) => api.get("/donation-schemas", { params }),
  create: (data) => api.post("/donation-schemas", data),
  update: (id, data) => api.put(`/donation-schemas/${id}`, data),
  delete: (id) => api.delete(`/donation-schemas/${id}`),
  bulkDelete: (data) => api.delete("/donation-schemas/bulk", { data }),
};

// WhatsApp Templates API
export const whatsappTemplatesAPI = {
  getAll: (params) => api.get("/whatsapp-templates", { params }),
  getById: (id) => api.get(`/whatsapp-templates/${id}`),
  create: (data) => api.post("/whatsapp-templates", data),
  update: (id, data) => api.put(`/whatsapp-templates/${id}`, data),
  delete: (id, name) => api.delete(`/whatsapp-templates/${id}`, { params: { name } }),
  preview: (id, sampleData) =>
    api.post(`/whatsapp-templates/${id}/preview`, { sampleData }),
  sendTestMessage: (data) => api.post("/whatsapp-templates/test-message", data),
};

export default api;
